/**
 * SIP Peer editor — schema, form types, helpers, and templates.
 *
 * Single source of truth for the peer editor form. The zod schema is derived
 * from the Core `PeerCreateInputBaseSchema` so form and API validation cannot
 * drift; only `sourceIpRanges` is relaxed to accept the textarea string form.
 */

import { z } from 'zod';
import { Building2, Phone, Wifi, Monitor, type LucideIcon } from 'lucide-react';
import { PeerCreateInputBaseSchema, peerCreateRefinements } from '@sbclite/core/schemas';
import type { Sip } from '@sbclite/core';
import type { SipPeerInfo } from '../../../types/sip';

// ========================================
// SCHEMA — Core schemas as single source of truth
// ========================================
// Form schema: same as Core but sourceIpRanges as string (textarea) | array,
// transformed to array. Business rules (sipInterfaceId, sourceIpRanges for
// carrier/teams, OPTIONS ping, identification) come from peerCreateRefinements.
export const sipPeerCreateSchema = PeerCreateInputBaseSchema.omit({ sourceIpRanges: true })
  .extend({
    sourceIpRanges: z
      .union([z.array(z.string()), z.string(), z.null()])
      .optional()
      .transform((val) => {
        if (val == null) return null;
        const arr = parseSourceIpRangesToArray(val);
        return arr.length > 0 ? arr : null;
      }),
  })
  .superRefine(peerCreateRefinements)
  .superRefine((data, ctx) => {
    // Static endpoints are required for peers we route to directly. Registered clients are
    // reached through their registration contact instead, so they do not need static endpoints.
    const endpoints = data.routingConfig?.endpoints;
    const needsStaticEndpoint = !data.registrationRequired && data.type !== 'softphone' && data.type !== 'webrtc';
    if (needsStaticEndpoint && (!Array.isArray(endpoints) || endpoints.length < 1)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['routingConfig', 'endpoints'],
        message: 'Add at least one peer endpoint',
      });
    }
  });

// Use shared type as base - single source of truth
export type SipPeer = SipPeerInfo;
// Form data: identical to the Core create input except `sourceIpRanges` is relaxed to also
// accept the textarea string form. We `Omit` + re-add (rather than intersect) so the field
// type stays a clean `string | string[] | null` instead of an unusable intersection.
export type SipPeerFormData = Omit<Sip.PeerCreateInput, 'sourceIpRanges'> & {
  sourceIpRanges?: string | string[] | null;
  tlsContextId?: string | null;
};

/**
 * Parse source IP ranges from textarea (string) or array to string[].
 * The textarea stores a string; backend and Review step expect string[].
 */
export function parseSourceIpRangesToArray(
  val: string | string[] | null | undefined
): string[] {
  if (val == null) return [];
  if (Array.isArray(val)) return val.filter(Boolean);
  return val
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Format source IP ranges array for textarea display (one per line). */
export function formatSourceIpRangesForInput(val: string[] | null | undefined): string {
  if (!val || !Array.isArray(val)) return '';
  return val.filter(Boolean).join('\n');
}

/** Crypto suites offered in the editor (SDES, RFC 4568). */
export const SRTP_CRYPTO_SUITES = [
  'AES_CM_128_HMAC_SHA1_80',
  'AES_CM_128_HMAC_SHA1_32',
  'AES_256_CM_HMAC_SHA1_80',
  'AES_256_CM_HMAC_SHA1_32',
] as const;

export const TLS_CIPHERS = [
  'TLS_AES_256_GCM_SHA384',
  'TLS_AES_128_GCM_SHA256',
  'ECDHE-RSA-AES256-GCM-SHA384',
  'ECDHE-RSA-AES128-GCM-SHA256',
] as const;

/** Preferred protocol per peer type (used to filter interfaces when creating). */
export const PREFERRED_PROTOCOL_BY_TYPE: Record<string, ('UDP' | 'TCP' | 'TLS')[]> = {
  teams: ['TLS'],
  carrier: ['UDP', 'TLS'],
  webrtc: ['TLS'],
  softphone: ['UDP', 'TCP', 'TLS'],
  pbx: ['TCP', 'UDP', 'TLS'],
};

type PeerMediaConfigForm = {
  srtp: 'disabled' | 'optional' | 'mandatory';
  cryptoSuites: string[];
  expandCryptoSuites: boolean;
  sdpStripIce: boolean;
  sdpOutboundProfile: 'passthrough' | 'carrier-basic-rtp';
};

/**
 * Derive the peer media-security policy for the form. The engine REQUIRES a valid SRTP
 * policy on every peer (it skips peers without one), so we always produce a concrete value.
 */
export function mediaConfigForForm(peer?: SipPeer | null): PeerMediaConfigForm {
  const mc = peer?.mediaConfig ?? null;
  const srtp: PeerMediaConfigForm['srtp'] =
    mc?.srtp === 'disabled' || mc?.srtp === 'mandatory' ? mc.srtp : 'optional';
  return {
    srtp,
    cryptoSuites:
      mc?.cryptoSuites && mc.cryptoSuites.length > 0 ? mc.cryptoSuites : ['AES_CM_128_HMAC_SHA1_80'],
    expandCryptoSuites: mc?.expandCryptoSuites ?? false,
    sdpStripIce: mc?.sdpStripIce ?? false,
    sdpOutboundProfile: mc?.sdpOutboundProfile ?? 'passthrough',
  };
}

export function metadataForForm(peer?: SipPeer | null): NonNullable<SipPeerFormData['metadata']> {
  return { ...(peer?.metadata ?? {}) };
}

// ========================================
// TEMPLATES (create-only quick setup)
// ========================================
export const CARRIER_TEMPLATES = {
  teams: {
    name: 'Microsoft Teams',
    icon: Building2,
    description: 'Direct routing connection to Microsoft Teams. SBCLite automatically emulates an AudioCodes Mediant VE SBC for Microsoft certification compatibility.',
    template: {
      type: 'teams' as const,
      authRequired: false,
      registrationRequired: false,
      routingConfig: {
        optionsPingEnabled: false
      },
      outboundTlsConfig: {
        verifyServer: true,
        minVersion: 'TLSv1.2' as const,
        sni: 'sip.pstnhub.microsoft.com'
      },
      metadata: {
        userAgent: 'Mediant VE SBC/v.7.40A.500.009',
        teamsConfig: {
          sbcFqdn: '',
          directRoutingDomains: [],
          audioCodes: {
            emulation: true,
            version: '7.40A.500.009',
            deviceModel: 'Mediant VE SBC'
          },
          certificateRequirements: {
            requireValidCert: true,
            trustedRoots: [],
            minimumKeySize: 2048
          }
        }
      }
    }
  },
  pstn: {
    name: 'PSTN Carrier',
    icon: Phone,
    description: '',
    template: {
      type: 'carrier' as const,
      authRequired: true,
      registrationRequired: true,
      authConfig: {
        digestAuth: true,
        algorithm: 'MD5' as const
      },
      routingConfig: {
        optionsPingEnabled: false
      },
      mediaConfig: {
        srtp: 'mandatory' as const,
        cryptoSuites: ['AES_CM_128_HMAC_SHA1_80'],
        expandCryptoSuites: false,
        sdpStripIce: true,
        sdpOutboundProfile: 'carrier-basic-rtp' as const
      },
      outboundTlsConfig: {
        verifyServer: true,
        minVersion: 'TLSv1.2' as const
      },
      metadata: {
        outboundMinSessionExpiresSeconds: 900
      }
    }
  },
  webrtc: {
    name: 'WebRTC Client',
    icon: Wifi,
    description: 'WebRTC client connection with TLS',
    template: {
      type: 'webrtc' as const,
      authRequired: true,
      registrationRequired: false,
      authConfig: {
        digestAuth: true,
        algorithm: 'SHA-256' as const
      }
    }
  },
  pbx: {
    name: 'PBX System',
    icon: Monitor,
    description: 'Internal PBX or enterprise communication system',
    template: {
      type: 'pbx' as const,
      authRequired: false,
      registrationRequired: false
    }
  }
} satisfies Record<string, { name: string; icon: LucideIcon; description: string; template: Partial<SipPeerFormData> }>;

export type TemplateKey = keyof typeof CARRIER_TEMPLATES;

// ========================================
// DEFAULT VALUES
// ========================================

/** Blank create defaults (no template). */
function blankDefaults(): SipPeerFormData {
  return {
    name: '',
    type: 'carrier',
    sipUri: '',
    domain: '',
    enabled: true,
    authRequired: false,
    registrationRequired: false,
    registrationPolicy: 'open',
    sourceIpRanges: '',
    username: null,
    realm: null,
    registrationUri: null,
    groupId: null,
    groupWeight: 100,
    groupPriority: 50,
    groupEnabled: true,
    codecGroupId: null,
    sipProfileId: null,
    tlsContextId: null,
    inboundManipulationId: null,
    outboundManipulationId: null,
    sipInterfaceId: null,
    mediaRealmId: undefined,
    hostId: null,
    mediaConfig: mediaConfigForForm(null),
    authConfig: null,
    routingConfig: null,
    outboundTlsConfig: null,
    metadata: null,
  };
}

/** Edit defaults derived from an existing peer. */
function editDefaults(peer: SipPeer): SipPeerFormData {
  return {
    name: peer.name,
    type: peer.type,
    sipUri: peer.sipUri ?? undefined,
    domain: peer.domain,
    enabled: peer.enabled,
    authRequired: peer.authRequired,
    registrationRequired: peer.registrationRequired,
    registrationPolicy: peer.registrationPolicy || 'open',
    username: peer.username,
    realm: peer.realm,
    registrationUri: peer.registrationUri,
    groupId: peer.groupId || null,
    groupWeight: peer.groupWeight ?? 100,
    groupPriority: peer.groupPriority ?? 50,
    groupEnabled: peer.groupEnabled ?? true,
    codecGroupId: peer.codecGroupId || null,
    sipProfileId: peer.sipProfileId || null,
    tlsContextId: peer.tlsContextId || null,
    inboundManipulationId: peer.inboundManipulationId || null,
    outboundManipulationId: peer.outboundManipulationId || null,
    sipInterfaceId: peer.sipInterfaceId || null,
    mediaRealmId: peer.mediaRealmId ?? undefined,
    hostId: peer.hostId || null,
    sourceIpRanges: formatSourceIpRangesForInput(peer.sourceIpRanges),
    mediaConfig: mediaConfigForForm(peer),
    authConfig: peer.authConfig,
    routingConfig: peer.routingConfig,
    outboundTlsConfig: (peer.outboundTlsConfig ?? null) as SipPeerFormData['outboundTlsConfig'],
    metadata: metadataForForm(peer),
  };
}

/**
 * Create defaults seeded from a template.
 *
 * Preserves the template's own metadata (e.g. Teams `userAgent` / `teamsConfig`
 * for AudioCodes emulation) and only adds the template description when the
 * template does not already provide one. This intentionally fixes the previous
 * `handleTemplateSelect`, which overwrote template metadata with a
 * description-only object and dropped the Teams emulation seed on create.
 */
function templateDefaults(key: TemplateKey): SipPeerFormData {
  const template = CARRIER_TEMPLATES[key];
  const templateMetadata = 'metadata' in template.template ? template.template.metadata : undefined;
  const templateHasDescription =
    typeof templateMetadata === 'object' && templateMetadata !== null && 'description' in templateMetadata;
  return {
    ...blankDefaults(),
    ...template.template,
    name: '',
    sipUri: '',
    domain: '',
    mediaConfig:
      'mediaConfig' in template.template && template.template.mediaConfig
        ? template.template.mediaConfig
        : mediaConfigForForm(null),
    metadata: {
      ...(templateMetadata ?? {}),
      ...(template.description && !templateHasDescription
        ? { description: template.description }
        : {}),
    },
  };
}

/**
 * Build the RHF default values for the editor.
 * - editingPeer set → edit defaults
 * - templateKey set → template-seeded create defaults
 * - otherwise → blank create defaults
 */
export function buildDefaultValues(opts: {
  editingPeer?: SipPeer | null;
  templateKey?: TemplateKey | null;
}): SipPeerFormData {
  if (opts.editingPeer) return editDefaults(opts.editingPeer);
  if (opts.templateKey) return templateDefaults(opts.templateKey);
  return blankDefaults();
}
